"use strict"

import React, { forwardRef } from "react"
import { scaleLinear } from "d3-scale"
import { extent } from "d3-array"
import { v4 as uuid } from "uuid"
import memoize from "memoize-one"

import { isNumber, arrayWithContents } from "../../../utils"
import { withAnnotationsConfig } from "../../../config/AnnotationConfigProvider"
import {
  getRoundedMinMax,
  getRangeWithoutOutliers,
  filterOnlineOfflineData,
  filterDataOnSampleType,
  getMatchingTrend,
  isHomeDashPlot,
} from "../../resources/utils"
import ObservationPlotCanvas from "./ObservationPlotCanvas"
import ObservationPlotSVG from "./ObservationPlotSVG"
import ObservationKeyMarker from "../GraphKeys/ObservationKeyMarker"
import LabelForSeries from "./LabelForSeries"
import { lowerBound, downsample } from "./utils"
import { id_accessor } from "../../../../components/shared_components_registration"
import AbstractPlot from "../AbstractPlot"

export const getGraphAnnotations = (graphKey, annotations, tags = []) => {
  return (!["multi", "site-wide"].includes(graphKey.special) && annotations ? annotations : []).filter(
    (a) => a.tags.length === 0 || a.tags.filter((value) => tags.includes(value)).length > 0,
  ) // Get graph specific annotations
}

class _ObservationPlot extends React.PureComponent {
  static defaultProps = {
    width: 600,
    height: 220,
    margin: {
      top: 15,
      bottom: 75,
      left: 80,
      right: 80,
    },
    useSVG: false,
    tickNum: null, // Can put a number here to specify a specific # of ticks on YAxis
  }

  constructor(props) {
    super(props)
    this.clipID = uuid()
    this.findDataForPoint = this.findDataForPoint.bind(this)
    this.getData = this.getData.bind(this)
    this.onCrossHairClick = this.onCrossHairClick.bind(this)
  }

  onCrossHairClick(clickX, clickY) {
    const { x, graphKey, zoomKey, avoid_annotations = false, annotation_click } = this.props
    const x_axis_value = x && x.invert(clickX)

    !avoid_annotations && annotation_click && annotation_click(x_axis_value, graphKey, zoomKey)
  }

  makeYaxis(outlier, height, layers, overrideDomain) {
    let minY, maxY
    if (!outlier) {
      let maxRange = extent(
        Array.prototype.concat.apply(
          [],
          layers.map((l) => l.data),
        ),
        (obs) => obs.value,
      )
      minY = maxRange[0]
      maxY = maxRange[1]
    } else {
      let observationExtents = []
      layers.forEach((obs) => {
        let [min, max] = getRangeWithoutOutliers(obs, (obs) => obs.value)
        observationExtents.push(min, max)
      })
      let outlierRange = extent(observationExtents)
      minY = outlierRange[0]
      maxY = outlierRange[1]
    }

    if (!isNumber(minY)) minY = 0
    if (!isNumber(maxY)) maxY = 10

    // Handle if Y-Axis Range is too close together to meaningfully visualize
    if (minY === maxY || Math.abs(maxY - minY) < 0.001) {
      minY = minY - 0.005
      maxY = maxY + 0.005
    }

    // Give a bit of a buffer above/below the calc'd range so edge cases don't fall right on the top/bottom of
    // the graph. Makes it easier to see on plot itself. Does not overwrite zoom ranges or set domains on graph.
    const axis_range = Math.abs(maxY - minY)
    minY = minY - axis_range * 0.05
    maxY = maxY + axis_range * 0.05
    ;[minY, maxY] = getRoundedMinMax(minY, maxY, height)

    let range = []
    if (!overrideDomain) {
      range = [minY, maxY]
    } else {
      range = [
        isNumber(overrideDomain[0]) ? overrideDomain[0] : minY,
        isNumber(overrideDomain[1]) ? overrideDomain[1] : maxY,
      ]
    }

    return scaleLinear().domain(range).range([height, 0])
  }

  makeAxis1 = memoize((outlier, height, overrideDomain, ...layers) =>
    this.makeYaxis(outlier, height, layers, overrideDomain),
  )
  makeAxis2 = memoize((outlier, height, overrideDomain, ...layers) =>
    this.makeYaxis(outlier, height, layers, overrideDomain),
  )

  getData(dataLayer, layer, graphKey) {
    if ((dataLayer && !dataLayer.data) || layer.disabled) {
      return {
        ...layer,
        ...dataLayer,
      }
    } else {
      let sortedData = Object.assign([], dataLayer?.data || [])
      sortedData = filterOnlineOfflineData(filterDataOnSampleType(sortedData, graphKey), graphKey)
      sortedData.sort((a, b) =>
        this.timeExtractor.difference(
          this.timeExtractor.getObservationTime(a),
          this.timeExtractor.getObservationTime(b),
        ),
      )
      return {
        ...layer,
        ...dataLayer,
        data: sortedData,
      }
    }
  }

  getDataLayers = memoize((layers, dataAccessors, timeExtractor) => {
    if (layers.special === "multi") {
      return (layers.observations || []).map((l) => {
        const observationAccessor = dataAccessors[l[id_accessor]]
        if (observationAccessor) {
          return this.getData(
            !l.disabled ? observationAccessor(l.tag, l.units, l.smp_src) || { pending: true } : {},
            l,
            layers,
          )
        }
      })
    } else if (layers.special === "site-wide") {
      const summaryPlot = isHomeDashPlot(layers)
      const obsAccessors = dataAccessors || {}
      const observations = layers.observations || []
      let dataLayers = []
      observations.forEach((t) => {
        Object.keys(obsAccessors).forEach((entity) => {
          let dataLayer
          let extra_info =
            "reactor" in dataAccessors[entity] ? { reactor: dataAccessors[entity]["reactor"] } : {}
          const observationAccessor = dataAccessors[entity]["observationAccessor"]
          !t.disabled && observationAccessor
            ? (dataLayer = observationAccessor(t.tag, t.units, t.smp_src) || { pending: true })
            : {}
          dataLayers.push(this.getData(dataLayer, summaryPlot ? { ...t, ...extra_info } : extra_info, layers))
        })
      })

      if (dataLayers.filter((l) => l.loading).length > 0) {
        return (layers?.tags || []).map((d) => {
          return { ...d, loading: true }
        })
      }

      if (summaryPlot) {
        return dataLayers.filter((l) => !l.disabled)
      }

      dataLayers = dataLayers.filter((l) => !l.pending && !l.loading)
      const reactorTags = layers.tags.filter((l) => l.tag !== "Design" && !l.disabled) || []
      const designTag = layers.tags.find((l) => l.tag === "Design" && !l.disabled)
      const sumTag = layers.tags.find((l) => l.tag === "Total Production" && !l.disabled)
      const aggTag = layers.tags.find((l) => l.tag === "All Reactors" && !l.disabled) || {}
      const design = layers.design ? observations[0].design : null
      const multiplier = layers.design ? observations[0].multiplier : null
      const aggregateTimestamps = !!(isNumber(design) && multiplier)

      const finalTags = []
      if (layers.all) {
        const dataLists = dataLayers.map((l) =>
          (l?.data || []).map((d) => {
            return { ...d, reactor: l.reactor }
          }),
        )
        const data = [].concat.apply([], dataLists)
        finalTags.push({ ...aggTag, data: data })
      } else {
        reactorTags.forEach((rt) => {
          const dataLists = dataLayers
            .filter((l) => l.reactor === rt.tag)
            .map((l) =>
              l.data.map((d) => {
                return { ...d, reactor: l.reactor }
              }),
            )
          const data = [].concat.apply([], dataLists)
          finalTags.push({
            ...rt,
            data: data.sort((a, b) => a.timestamp.valueOf() - b.timestamp.valueOf()),
          })
        })
      }

      if (!aggregateTimestamps) {
        if (isNumber(design) && designTag) {
          const dataLists = [].concat
            .apply(
              [],
              finalTags.map((l) => l.data),
            )
            .map((d) => d.timestamp)
          const [minTime, maxTime] = extent(dataLists)
          const min_info = {
            timestamp: minTime,
            value: design,
          }
          const max_info = {
            timestamp: maxTime,
            value: design,
          }
          designTag["data"] = [min_info, max_info]

          return [...finalTags, designTag]
        } else {
          return finalTags
        }
      } else {
        // Need to generate design line/summed metrics for box plots
        let tag_sum = 0
        let sum_observations = []
        let designArray = []
        let finalAllObservation = []
        const allData = [].concat
          .apply(
            [],
            finalTags.map((l) => l.data),
          )
          .sort((a, b) => a.timestamp.valueOf() - b.timestamp.valueOf())
        const uniqueTimestamps = [...new Set(allData.map((d) => String(d.timestamp)))]
        uniqueTimestamps.forEach((ts) => {
          const timestampGroup = allData.filter((d) => String(d.timestamp) === ts)
          const summed_day_value = timestampGroup.map((d) => d.value).reduce((a, b) => a + b)

          if (isNumber(design) && designTag && design !== 0) {
            const designValue =
              timestampGroup.filter((d) => layers.design_reactors.includes(d.reactor)).length *
              multiplier *
              design
            designArray.push({
              timestamp: timestampGroup[0].timestamp,
              value: designValue,
            })
          }

          if (sumTag) {
            tag_sum += summed_day_value
            sum_observations.push({
              timestamp: timestampGroup[0].timestamp,
              value: tag_sum,
            })
          }
          if (layers.all) {
            finalAllObservation.push({
              timestamp: timestampGroup[0].timestamp,
              value: summed_day_value,
            })
          }
        })

        if (layers.all) {
          finalTags[0].data = finalAllObservation.sort(
            (a, b) => a.timestamp.valueOf() - b.timestamp.valueOf(),
          )
        }

        let toReturn = [...finalTags]
        if (designTag && arrayWithContents(designArray)) {
          let designSupplement = []
          designArray.sort((a, b) => a.timestamp.valueOf() - b.timestamp.valueOf())
          designArray.forEach((d, idx) => {
            if (idx > 0) {
              let prev_day = designArray[idx - 1].timestamp
              let curr_day = d.timestamp
              let diff = curr_day - prev_day
              while (diff > 86400000) {
                prev_day = timeExtractor.offsetDays(prev_day, 1)
                designSupplement.push({ timestamp: prev_day, value: 0 })
                diff = curr_day - prev_day
              }
            }
          })
          designArray = [...designArray, ...designSupplement]
          designArray.sort((a, b) => a.timestamp.valueOf() - b.timestamp.valueOf())
          designTag.data = designArray
          toReturn = [...toReturn, designTag]
        }

        if (sumTag) {
          sumTag.data = sum_observations.sort((a, b) => a.timestamp.valueOf() - b.timestamp.valueOf())
          toReturn = [...toReturn, sumTag]
        }

        return toReturn
      }
    } else if (layers.special === "rnaseq") {
      const { samples = [], rnaseq = [] } = this.props
      const observations = layers?.observations || []
      const genes = observations.map((gene) => gene.tag)
      let geneInfo = rnaseq.filter((entry) => genes.includes(entry["gene__name"]))
      let dataLayer = []
      observations.forEach((gene) => {
        let data = []
        geneInfo
          .filter((d) => d["gene__name"] === gene.tag)
          .forEach((obvs) => {
            const sample = samples.find((smp) => smp.id === obvs.sample_id) || {}
            data.push({
              tag: obvs["gene__name"],
              units: "tpm",
              value: obvs.tpm,
              day: sample.day,
              timestamp: sample.taken,
            })
          })
        data.sort((a, b) =>
          this.timeExtractor.difference(
            this.timeExtractor.getObservationTime(a),
            this.timeExtractor.getObservationTime(b),
          ),
        )
        dataLayer.push({
          ...gene,
          data: data,
        })
      })
      return dataLayer
    } else {
      const dataAccessor = dataAccessors
      return (layers?.observations || []).map((l) => {
        let dataLayer
        if (dataAccessor && typeof dataAccessor === "function") {
          !l.disabled ? (dataLayer = dataAccessor(l.tag, l.units, l.smp_src) || { pending: true }) : {}
          return this.getData(dataLayer, l, layers)
        }
      })
    }
  })

  getFilteredLayers = memoize((layers) => layers.filter((l) => l && arrayWithContents(l.data)))
  getActiveLayers = memoize((layers, disabledSeries) =>
    (layers || []).map((l) => {
      if (disabledSeries.find((d) => getMatchingTrend(d, l))) {
        const { data, ...info } = l
        return info
      } else {
        return l
      }
    }),
  )

  render() {
    const {
      canvasRef,
      width,
      margin,
      height,
      timeExtractor,
      zoomRanges,
      baseTime,
      graphKey,
      x,
      download,
      liveView,
      observationAccessors,
      layers,
      crossRunActions = [], // special === "multi" plot
      runInoculationDate,
      actions,
      forceReloadPlotData,
      tickNum,
      isSuppPlot,
      annotations,
      useSVG,
      titleFontSize,
      graphTitleSupplement,
      zoomKey,
      site_actions,
      runEventLabels = [],
      avoid_annotations = false,
    } = this.props

    this.timeExtractor = timeExtractor
    const y1OutlierFilter = graphKey && graphKey.outlierFilter,
      y2OutlierFilter = graphKey && graphKey.outlierFilter2,
      forceYdomain = zoomRanges?.y,
      forceY2domain = zoomRanges?.y2
    const tags = arrayWithContents(layers.observations) ? layers.observations.map((l) => l.tag) : [] // To encourage layers update
    const run_annotations = getGraphAnnotations(layers, annotations, tags)
    const dataLayers = (this.getDataLayers(layers, observationAccessors, timeExtractor, tags) || []).filter(
      (l) => l,
    )
    const disabledSeries = (layers?.observations || []).filter((l) => l.disabled)
    const activeLayers = this.getActiveLayers(dataLayers, disabledSeries)
    const errorLayers = activeLayers.filter((l) => l.err)
    const filteredLayers = this.getFilteredLayers(activeLayers)
    const loading = activeLayers.some((l) => l.loading)
    const axis1layers = filteredLayers.filter((l) => !l.axis2)
    const axis2layers = filteredLayers.filter((l) => l.axis2)

    const y1 = this.makeAxis1(y1OutlierFilter, height, forceYdomain || layers.axis1domain, ...axis1layers)
    const y2 = this.makeAxis2(y2OutlierFilter, height, forceY2domain || layers.axis2domain, ...axis2layers)
    let boxLayers = filteredLayers.filter((layer) => layer.style === "box")
    const canvasGraph = !useSVG || (boxLayers && boxLayers.length > 0)

    return (
      <AbstractPlot
        clipID={this.clipID}
        width={width}
        height={height}
        margin={margin}
        timeExtractor={timeExtractor}
        x={x}
        xAxisLabel={graphKey.xAxisLabel ?? undefined}
        showY1={axis1layers.length > 0}
        y1={y1}
        y1AxisLabel={graphKey.axis1label}
        showY2={axis2layers.length > 0}
        y2={y2}
        y2AxisLabel={graphKey.axis2label}
        zoomKey={zoomKey}
        baseTime={baseTime}
        showGraphTitle={liveView && !download}
        graphKey={graphKey}
        graphTitleSupplement={graphTitleSupplement}
        titleFontSize={titleFontSize}
        showSVGGridlines={(layers?.gridlines || false) && !canvasGraph}
        isSuppPlot={isSuppPlot}
        tickNum={tickNum}
        PlotCanvas={
          <>
            {canvasGraph && !loading && (
              <ObservationPlotCanvas
                ref={canvasRef}
                timeExtractor={timeExtractor}
                width={width}
                height={height}
                margin={margin}
                x={x}
                y={axis1layers.length === 0 ? y2 : y1}
                y1={y1}
                y2={y2}
                layers={filteredLayers}
                gridlines={layers.gridlines}
                plot_type={layers.special}
                runEventLabels={runEventLabels}
                runID={zoomKey}
              />
            )}
          </>
        }
        PlotSVG={
          <>
            {!canvasGraph && (
              <ObservationPlotSVG
                x={x}
                y1={y1}
                y2={y2}
                loading={loading}
                plot_type={layers.special}
                filteredLayers={filteredLayers}
                clipPath={`url(#${this.clipID})`}
                runEventLabels={runEventLabels}
                runID={zoomKey}
              />
            )}
          </>
        }
        download={download}
        loading={loading}
        showActionsAnnotations={!["site-wide"].includes(graphKey.special)}
        runInoculationDate={runInoculationDate}
        actions={
          layers.special === "multi"
            ? crossRunActions.filter((a) => ["run_action", "run_action_critical"].includes(a.action_type))
            : actions
        }
        site_actions={
          layers.special === "multi"
            ? crossRunActions.filter((a) => a.action_type === "site_action")
            : site_actions
        }
        annotations={
          layers.special === "multi"
            ? crossRunActions.filter((a) =>
                ["not_tag_specific_ano", "tag_specific_ano"].includes(a.action_type),
              )
            : run_annotations
        }
        onCrossHairClick={this.onCrossHairClick}
        findDataForPoint={this.findDataForPoint}
        forceReloadPlotData={forceReloadPlotData}
        errorLayers={errorLayers}
        runEventLabels={runEventLabels}
        avoid_annotations={avoid_annotations}
      />
    )
  }

  findDataForPoint(xpos, ypos, downsampleObservations) {
    const { x, timeExtractor, observationAccessors, observationTypes, layers } = this.props

    const dataLayers = this.getDataLayers(layers, observationAccessors, timeExtractor)

    const filteredLayers = dataLayers.filter((l) => l && l.data)

    const data = []

    const xTime = x.invert(xpos)
    for (const layer of filteredLayers) {
      if (!layer.data || layer.data.length === 0 || layer.disabled) continue
      let refLayer = { ...layer }
      // TODO - Technically more accurate to 'downSample' for Crosshairs Tooltip as well to match actual plotted data,
      //  but it seems to cause a noticeable detrimental impact on user experience to run 'downSample' method for each
      //  mouse movement.
      // if (downsampleObservations){
      //   refLayer = downsample(layer, layers.special, x, timeExtractor, 1.0)
      // }
      const closestObs =
        refLayer.data[lowerBound(refLayer.data, xTime, timeExtractor, refLayer?.trendDayOffset)]
      data.push({
        ...closestObs, // provides day, timestamp, value, and ?note
        ...refLayer,
        label: <LabelForSeries obs={refLayer} observationTypes={observationTypes} />,
        marker: <ObservationKeyMarker {...refLayer} />,
      })
    }
    return data
  }
}

const ObservationPlot = withAnnotationsConfig(_ObservationPlot)
export default forwardRef((props, ref) => <ObservationPlot canvasRef={ref} {...props} />)
